import { FC, useState } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { customTokens } from '../../styles';

export interface Log {
    agentName: string;
    content: string;
    timestamp: string;
    tokenUsage?: { Input: number; Output: number; Total: number };
    agentLogType: string;
    duration?: string | null;
}

const useStyles = makeStyles({
    card: {
        background: customTokens.colorContentWrapper,
        marginBottom: '18px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        position: 'relative',
        borderLeft: `6px solid ${customTokens.colorPaletteTealForeground2}`,
    },
    border1: {
        borderLeft: `6px solid ${customTokens.colorPaletteGreenForeground2}`,
    },
    border2: {
        borderLeft: `6px solid ${customTokens.colorMeBackground}`,
    },
    border3: {
        borderLeft: `6px solid ${customTokens.colorPaletteDarkOrangeForeground2}`,
    },
    border4: {
        borderLeft: `6px solid ${customTokens.colorPalettePurpleForeground2}`,
    },
    border: {
        borderLeft: `6px solid ${customTokens.colorContentWrapper}`,
    },
    agentName: {
        fontWeight: '700',
        color: customTokens.colorMeBackground,
        fontSize: '17px',
    },
    content: {
        marginBottom: '6px',
        fontSize: '15px',
        position: 'relative',
        minHeight: '24px',
        lineHeight: '1.6',
        whiteSpace: 'pre-line',
        color: customTokens.colorCustomText,
    },
    timestamp: {
        color: customTokens.colorTextDisabled,
        fontSize: '13px',
        marginBottom: '2px',
        letterSpacing: '0.2px',
    },
    moreButton: {
        background: 'none',
        border: 'none',
        color: customTokens.colorMeBackground,
        cursor: 'pointer',
        fontSize: '13px',
        padding: '0',
        marginLeft: '8px',
        verticalAlign: 'baseline',
        position: 'absolute',
        right: '0',
        bottom: '0',
    },
    duration: {
        position: 'absolute',
        top: '16px',
        right: '20px',
        background: tokens.colorPaletteYellowBackground1,
        color: tokens.colorPaletteYellowForeground1,
        borderRadius: '6px',
        padding: '2px 10px',
        fontWeight: '600',
        fontSize: '14px',
        boxShadow: '0 1px 4px rgba(234,179,8,0.08)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        zIndex: '1',
    },
    tokenUsage: {
        display: 'flex',
        gap: '16px',
        marginTop: '6px',
        fontSize: '11px',
        fontWeight: '500',
    },
    tokenInput: {
        color: tokens.colorPalettePurpleForeground2,
    },
    tokenOutput: {
        color: tokens.colorPaletteGreenForeground2,
    },
    tokenTotal: {
        color: tokens.colorPaletteBlueForeground2,
    },
    agentNameContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '2px',
    },
});

export const AgentLogCard: FC<{ log: Log }> = ({ log }) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);
    const isLong = log.content.length > 120;
    const shownContent = !isLong || expanded ? log.content : log.content.slice(0, 120) + '...';

    const renderTokenUsage = () => {
        if (!log.tokenUsage) return null;
        const { Input, Output, Total } = log.tokenUsage;
        if (Input === 0 && Output === 0 && Total === 0) return null;
        return (
            <div className={classes.tokenUsage}>
                <span className={classes.tokenInput}>Input: {Input}</span>
                <span className={classes.tokenOutput}>Output: {Output}</span>
                <span className={classes.tokenTotal}>Total: {Total}</span>
            </div>
        );
    };

    return (
        <div className={mergeClasses(classes.card, classes[`border${log.agentLogType}` as keyof typeof classes])}>
            {log.duration && <span className={classes.duration}>⏱️ {log.duration}</span>}
            <div className={classes.agentNameContainer}>
                <span className={classes.agentName}>{log.agentName}</span>
            </div>
            <div className={classes.content}>
                {shownContent}
                {isLong && (
                    <button
                        className={classes.moreButton}
                        onClick={() => {
                            setExpanded((prev) => !prev);
                        }}
                    >
                        {expanded ? 'Less' : 'More...'}
                    </button>
                )}
            </div>
            <div className={classes.timestamp}>{new Date(log.timestamp).toLocaleString()}</div>
            {renderTokenUsage()}
        </div>
    );
};
